/**
 * The PIECE BRIEF — the terminal product of the plan-time pipeline.
 *
 * Two things it deliberately does NOT contain: final platform prose, and any
 * media. Media generation is the largest single cost in the product and it is
 * spent only on briefs a human accepted, so this pipeline stops here on
 * purpose. A run that ends at "Draft" is the pipeline working, not failing.
 *
 * `visualDirection` is a CONTRACT, not an asset: it says what a picture would
 * have to show, and it carries a `refusal` for the cases where no honest
 * picture can be generated at all (a screenshot with no real capture, a chart
 * with invented numbers).
 */

import type { Arc } from './arc';
import type { LaneId } from './lanes';
import type { OfferClassId } from './offer-class';
import type { ProblemDistance, ReaderState, Saturation } from './reader-state';

import type { PlatformId } from '@/constants/platform-specs';

export type PieceFormat = 'single' | 'carousel' | 'video' | 'text';

export type CtaAsk = 'buy' | 'enquire' | 'read' | 'reply' | 'save' | 'follow';

export type ProofKind = 'metric' | 'quote' | 'case' | 'artefact' | 'none';

export interface EvidenceRef {
  /** Where it came from — a source sentence, a metric row, a review row. */
  kind: 'source_claim' | 'metric' | 'review' | 'brand_voice' | 'competitor' | 'direction';
  /** The verbatim text. Never a paraphrase — the whole point is checkability. */
  quote: string;
}

export type VisualIntent =
  'product_hero' | 'in_use' | 'scene' | 'portrait' | 'artefact' | 'diagram' | 'screenshot' | 'none';

/**
 * Every key a stored `textPlan` may carry. The Concept reader and the guard that ties this type to
 * `is_brief_concept_visual_direction` both read this list, so a new key cannot reach one and not the
 * other (ITC runs 6 and 7 each failed on one side of that gap).
 */
export const VISUAL_DIRECTION_TEXT_PLAN_KEYS = ['mode', 'headline', 'sourceLine'] as const;

export interface VisualDirection {
  intent: VisualIntent;
  subject: string;
  setting: string;
  negativeSpace: 'upper-third' | 'lower-third' | 'left-column' | 'right-column' | 'none';
  textPlan: {
    mode: 'overlay' | 'baked' | 'none';
    headline: string | null;
    /**
     * The line under the headline that says where its words come from
     * ("Source: itctax.com"). Present when the planner composed the headline
     * from a source; absent otherwise, and never invented by the renderer.
     */
    sourceLine?: string | null;
  };
  /**
   * Set when no honest image can be produced. A model-rendered UI screenshot
   * or a chart with numbers is fabricated by construction, so the brief asks
   * for a real capture instead.
   */
  refusal: string | null;
}

export type GroundingSlot =
  | 'source'
  | 'brand'
  | 'voice'
  | 'audiences'
  | 'winners'
  | 'patterns'
  | 'competitors'
  | 'references'
  | 'angleBank';

export type GroundingReport = Record<
  GroundingSlot,
  { kind: 'found' | 'partial' | 'none'; note: string }
>;

export interface PieceBrief {
  angleId: string | null;
  audienceId: string | null;
  audienceName: string | null;
  lane: LaneId;
  offerClass: OfferClassId;
  reader: { state: ReaderState; distance: ProblemDistance; saturation: Saturation };
  arc: Arc;
  platform: PlatformId;
  format: PieceFormat;
  /** The first line, written. */
  hook: string;
  /** What the body argues, in order. NOT final prose. */
  bodyDirection: string;
  ctaIntent: { ask: CtaAsk; line: string };
  proof: { kind: ProofKind; ref: string | null; quote: string | null };
  objection: string | null;
  visualDirection: VisualDirection;
  evidence: EvidenceRef[];
}

/** The six opening moves, for the slate's anti-sameness rule. */
export type OpeningMove = 'number' | 'question' | 'statement' | 'story' | 'quote' | 'instruction';

const INSTRUCTION_VERBS =
  /^(stop|start|try|use|do|don'?t|never|always|check|read|open|write|call|book|ask|take|make|pick|swap|cut|add)\b/i;
const STORY_OPENERS =
  /^(last|yesterday|in \d{4}|when i|when we|a client|a customer|three years|two years|one of our|i once|we once|the first time)\b/i;

/**
 * Deterministic. The slate rule that uses it ("no two consecutive pieces open
 * the same way") is only worth having if the classification cannot drift.
 */
export function openingMove(hook: string): OpeningMove {
  const text = hook.trim();
  if (!text) return 'statement';
  if (/^["“']/.test(text)) return 'quote';
  if (text.endsWith('?')) return 'question';
  if (/^[^a-zA-Z]*\d/.test(text)) return 'number';
  if (STORY_OPENERS.test(text)) return 'story';
  if (INSTRUCTION_VERBS.test(text)) return 'instruction';
  return 'statement';
}
